import styled, { css } from 'styled-components';
import getGradientType from '../../utils/get-Gradient-Types';
import getSubjectsType from '../../utils/get-Subject-Types';

interface PropTypes {
  $subject: string
  $islesson?: boolean
  $isbncc?: boolean
  $iscompetences?: boolean
}

interface containerProps {
  title?: any;
}

export const Title = styled.section<PropTypes>`
  margin-bottom: 1em;
  display: flex;
  flex-wrap: wrap;

  ${({ theme }) => theme.spacing.articleHeader}

  @media (max-width: 600px){
    font-size: 1.25rem;
  }

  ${({ $islesson, $subject, theme }) => ($islesson
    ? css`
      ${theme.typography.lesson}
      color: ${getSubjectsType({ color: 20, subject: $subject, theme })};`
    : css`
      ${theme.typography.h3}
      color: ${getSubjectsType({ color: 20, subject: $subject, theme })};
  `)
};

  &:after{
    content: '';
    display: block;
    ${({ theme, $subject }) => getGradientType($subject, theme, false)};
    height: 30px;
    width: 100%;
    border-radius: 12px;
    height: 4px;
    margin-top: 13px;
  }
`;

export const WrapIcon = styled.div`
  margin-right: 10px;
`;

export const Children = styled.section<PropTypes>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  color: ${({ theme }) => theme.colors.grayscale[20]};
  ${({ theme }) => theme.typography.normal};
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  margin-bottom: 2em;

  ${({ $isbncc }) => $isbncc &&
    css`
      ${({ theme }) => theme.spacing.articleBody}
      column-gap: 45px;
      row-gap: 15px;

      @media (max-width: 999px){
        column-gap: 45px;
      }

      @media (max-width: 600px){
        column-gap: 5px;
      }
    `
  };

  p, ul, .wrap-image {
    &:empty{
      display: none;
    }

    ${({ theme }) => theme.spacing.articleBody};

    @media (max-width: 999px){
      column-gap: 45px;
    }
  }

  .wrap-image.medio, .wrap-image.pequeno{
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    img, small{
      width: 60%;
    }
  }

  .wrap-image.pequeno{
    img, small{
      width: 40%;
    }
  }

  .realceAmarelo { background-color: #fffcc1; color: #333; border-radius: 4px; padding: 0 5px; }
  .realceAzul { background-color: #cbf0fe; color: #333; border-radius: 4px; padding: 0 5px; }
  .realceRosa { background-color: #ffdbf7; color: #333; border-radius: 4px; padding: 0 5px; }
  .realceTurquesa { background-color: #ccffff; color: #333; border-radius: 4px; padding: 0 5px; }
  .realceVerde { background-color: #e2ffca; color: #333; border-radius: 4px; padding: 0 5px; }
  .realceVermelho { background-color: #ffd8d8; color: #333; border-radius: 4px; padding: 0 5px; }

  img{
    border-radius: 20px;
    margin-top: .5em;
    cursor: pointer;
    objectFit: cover;
    width: 100%;
  }

  small{
    display: block;
    margin-bottom: 1.5em;
  }

  @media (max-width: 600px){
    font-size: 17px;
    line-height: normal;
  }

  .listaNumerica li::marker, .listaBullets li::marker {
    color: #F05A22;
  }

  .listaBullets li, p {
    ${({ $iscompetences, $subject, theme }) => $iscompetences &&
      css`
        display: flex;
        gap: .4em;
        list-style: none;
        transform: translateX(-1.5em);

        strong {
          color: ${getSubjectsType({ color: 20, subject: $subject, theme })};
          font-weight: 800;
          min-width: 26px;
        }
      `
  };
  }

  ${({ $iscompetences }) => $iscompetences &&
    css`
      p {
        transform: none;
      }
    `
  };

  .listaNumerica, .listaBullets{
    padding: 0 230px;
    ${({ theme }) => theme.typography.normal};

    @media (max-width: 1440px){
      padding: 0 170px;
      padding-left: calc(170px + 1.5em) !important;
    }

    @media (max-width: 999px){
      padding: 0 92px;
      padding-left: calc(92px + 1.5em) !important;
      column-gap: 45px;
    }

    @media (max-width: 600px){
      padding: 0 20px;
      padding-left: calc(20px + 1.5em) !important;
    }
  }

  .listaNumerica li, .listaBullets li {
    margin-bottom: .5em;
    ${({ $isbncc }) => $isbncc &&
    css`
        list-style: none;
        min-width: 20%;
        cursor: pointer;

        @media (max-width: 600px){
          margin-right: 20px;
        }
      `
  };
  }

  a:not([href]){
    display: none;
  }

  a{
    text-decoration: underline;
    overflow-wrap: break-word;
    color: #F05A22;
    font-weight: 700;

    // TODO: quando tivermos um exemplo de algum termo de glossário, ele também entrará nesse estilo
    ${({ $isbncc, $subject, theme }) => $isbncc &&
      css`
        border-radius: 4px;
        padding: 0 4px;
        border-bottom: 3px solid ${getSubjectsType({ color: 30, subject: $subject, theme })};
        background: ${getSubjectsType({ color: 50, subject: $subject, theme })};
      `}
  }

  .imageLegend {
    ${({ theme }) => theme.typography.font}
    margin-top: -1em;
    padding: 0;
  }

  h4 {
    ${({ theme }) => theme.typography.h4}
    margin: 0 90px;

    @media (max-width: 600px){
      margin: 0 20px;
    }

    padding: 24px 0 16px 0;
    border-bottom: 3px solid ${({ theme, $subject }) => getSubjectsType({ theme, color: 30, subject: $subject })};
  }

  h5 {
    ${({ theme }) => theme.typography.h5}
    margin: 36px 90px 16px 90px;

    @media (max-width: 600px){
      margin: 16px 20px 8px 20px;
    }
  }

  h5 + h6 {
    margin-top: 0;
  }

  h6 {
    ${({ theme }) => theme.typography.h6}
    margin: 16px 90px 0 90px;

    @media (max-width: 600px){
      margin: 12px 20px 6px 20px;
    }
  }
`;

export const Container = styled.section<containerProps>`
  ${({ title }) => title === 'Habilidades (BNCC)' &&
    css`
      & > * {
        display: flex;
        flex-wrap: wrap;
      }
    `}
`;
